import React, { useMemo } from 'react';
import {
  ReactFlow,
  MarkerType,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  Handle,
  Position,
  type Node,
  type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { cn } from '@/lib/utils';
import { autoLayout } from '../services/diagram.service';
import { NodeCard, getNodeColor, getNodeIcon } from './NodeCard';
import type { BuildTreeNode, BuildTreeEdge } from '../services/pipelineNode.service';

interface NodeTreeViewProps {
  nodes: BuildTreeNode[];
  edges: BuildTreeEdge[];
  className?: string;
  /** Called when a node is clicked. Receives the object_id (or pipeline_node_id for backwards compatibility) */
  onNodeClick?: (objectId: string, objectType?: string) => void;
}

// Custom node component using shared NodeCard
const CustomNode = ({ data }: { data: any }) => {
  const { title, nodeType, onNodeClick, pipelineNodeId, objectId, objectType } = data;

  const nodeWidth = 320;
  const nodeHeight = 48;

  const handleClick = () => {
    if (onNodeClick) {
      // Use objectId if available, otherwise fall back to pipelineNodeId for backwards compatibility
      const id = objectId || pipelineNodeId;
      if (id) {
        onNodeClick(id, objectType);
      }
    }
  };

  const nodeColor = getNodeColor(nodeType);

  return (
    <NodeCard
      title={title}
      nodeType={nodeType}
      width={nodeWidth}
      height={nodeHeight}
      onClick={handleClick}
      customIcon={getNodeIcon(nodeType, nodeColor, 'sm')}
    >
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Right} />
    </NodeCard>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

function makeReactFlowEdge(edge: BuildTreeEdge): Edge {
  return {
    id: edge.id,
    source: edge.from_id,
    target: edge.to_id,
    type: 'simplebezier',
    animated: false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: '#64748b',
    },
    style: {
      stroke: '#64748b',
      strokeWidth: 2,
    },
  };
}

// Layout calculation using dagre
const calculateLayout = (
  nodes: BuildTreeNode[],
  edges: BuildTreeEdge[],
  onNodeClick?: (objectId: string, objectType?: string) => void
) => {
  const nodeWidth = 320;
  const nodeHeight = 48;

  const reactFlowNodes: Node[] = nodes.map((node) => ({
    id: node.id,
    type: 'custom',
    position: { x: 0, y: 0 },
    data: {
      title: node.title,
      nodeType: node.type,
      pipelineNodeId: node.pipeline_node_id,
      objectId: node.object_id,
      objectType: node.object_type,
      onNodeClick,
    },
  }));

  const reactFlowEdges: Edge[] = edges.map((edge) => ({
    id: edge.id,
    source: edge.from_id,
    target: edge.to_id,
  }));

  const nodePositions = autoLayout(reactFlowNodes, reactFlowEdges, 'LINEAR', nodeWidth, nodeHeight, 150, 30);

  const positionedNodes = reactFlowNodes.map((node) => ({
    ...node,
    position: nodePositions[node.id] || { x: 0, y: 0 },
  }));

  return positionedNodes;
};

export const NodeTreeView: React.FC<NodeTreeViewProps> = ({
  nodes: treeNodes,
  edges: treeEdges,
  className,
  onNodeClick,
}) => {
  // Calculate layout
  const initialNodes = useMemo(() => {
    return calculateLayout(treeNodes, treeEdges, onNodeClick);
  }, [treeNodes, treeEdges, onNodeClick]);

  // Convert edges to react-flow format
  const initialEdges: Edge[] = useMemo(
    () => treeEdges.map(makeReactFlowEdge),
    [treeEdges]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes/edges when tree data changes
  React.useEffect(() => {
    const newNodes = calculateLayout(treeNodes, treeEdges, onNodeClick);
    setNodes(newNodes);

    setEdges(treeEdges.map(makeReactFlowEdge));
  }, [treeNodes, treeEdges, onNodeClick, setNodes, setEdges]);

  return (
    <div className={cn('w-full h-full', className)}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={true}
        proOptions={{ hideAttribution: true }}
      />
    </div>
  );
};
